import React from 'react';
import {
    Box, Typography, TextField, Grid, ToggleButton, ToggleButtonGroup, IconButton,
} from '@mui/material';
import { PersonSearch as PersonSearchIcon, Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material';
import useDictation from '../../../../hooks/useDictation';
import { MAINTENANCE_SAFETY_ITEMS } from '../constants';
import SelectSingleEmployeeModal from '../../../shared/SelectSingleEmployeeModal';

const toggleSx = {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
        px: 2, py: 0.5, fontSize: '0.75rem', textTransform: 'none',
        border: '1px solid #bdbdbd', minHeight: 32,
        '&.Mui-selected': { fontWeight: 600, border: '1px solid', '&:hover': { opacity: 0.9 } },
        '&:not(.Mui-selected)': { color: '#666' },
    },
};

export default function MaintenanceSafetyForm({ results, onChange, isReadOnly, inspectionDate }) {
    const [inspectorModalOpen, setInspectorModalOpen] = React.useState(false);

    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000, prime: true,
    });

    const toggleCommentsDictation = async () => {
        if (!dictationSupported) return;
        if (isListening) { stopDictation(); return; }
        await startDictation({
            onResult: (transcript) => {
                if (!transcript) return;
                const cur = results.comments || '';
                const space = cur && !cur.endsWith(' ') ? ' ' : '';
                onChange({ ...results, comments: cur + space + transcript });
            },
        });
    };

    React.useEffect(() => {
        if (!results.maintenanceInspector) {
            const name = localStorage.getItem('employeeFullName') || '';
            if (name) onChange({ ...results, maintenanceInspector: name });
        }
    }, []);

    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const dateObj = inspectionDate ? new Date(inspectionDate + 'T00:00:00') : new Date();
    const monthName = months[dateObj.getMonth()];
    const year = dateObj.getFullYear();

    const half = Math.ceil(MAINTENANCE_SAFETY_ITEMS.length / 2);
    const col1 = MAINTENANCE_SAFETY_ITEMS.slice(0, half);
    const col2 = MAINTENANCE_SAFETY_ITEMS.slice(half);

    const renderItem = (item, idx) => {
        const val = results[item.key] || '';
        return (
            <Box key={item.key} sx={{
                p: 0.65, border: '1px solid', borderColor: val === 'fail' ? '#ffcdd2' : '#e0e0e0',
                backgroundColor: val === 'fail' ? '#fff5f5' : '#fafafa',
                display: 'flex', alignItems: 'center', gap: 0.65, mb: 0.5, borderRadius: 0.5,
            }}>
                <Typography variant="body2" sx={{ fontSize: '0.725rem', lineHeight: 1.3, flex: 1, minWidth: 0 }}>
                    {item.label}
                </Typography>
                {!isReadOnly ? (
                    <ToggleButtonGroup value={val} exclusive
                        onChange={(_, v) => { if (v !== null) onChange({ ...results, [item.key]: v }); }}
                        size="small" sx={toggleSx}>
                        <ToggleButton value="pass" sx={{ '&.Mui-selected': { backgroundColor: '#4caf50 !important', color: '#fff !important', borderColor: '#4caf50 !important', '&:hover': { backgroundColor: '#45a049 !important' } } }}>P</ToggleButton>
                        <ToggleButton value="fail" sx={{ '&.Mui-selected': { backgroundColor: '#f44336 !important', color: '#fff !important', borderColor: '#f44336 !important', '&:hover': { backgroundColor: '#e53935 !important' } } }}>F</ToggleButton>
                        <ToggleButton value="na" sx={{ '&.Mui-selected': { backgroundColor: '#9e9e9e !important', color: '#fff !important', borderColor: '#9e9e9e !important', '&:hover': { backgroundColor: '#888 !important' } } }}>NA</ToggleButton>
                    </ToggleButtonGroup>
                ) : (
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', flexShrink: 0,
                        color: val === 'pass' ? '#4caf50' : val === 'fail' ? '#f44336' : '#9e9e9e' }}>
                        {val ? val.toUpperCase() : '—'}
                    </Typography>
                )}
            </Box>
        );
    };

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#558b2f', mb: 1.5, fontSize: '0.9rem' }}>
                DAILY & MONTHLY MAINTENANCE & SAFETY LOG
            </Typography>

            <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                    <TextField label="Equipment #" size="small" fullWidth disabled={isReadOnly}
                        value={results.equipmentNumber || ''} onChange={e => onChange({ ...results, equipmentNumber: e.target.value })} />
                </Grid>
                <Grid item xs={3}>
                    <TextField label="Hour Meter" size="small" fullWidth disabled={isReadOnly}
                        value={results.hourMeter || ''} onChange={e => onChange({ ...results, hourMeter: e.target.value })} />
                </Grid>
                <Grid item xs={3}>
                    <TextField label="Date" type="date" size="small" fullWidth disabled={isReadOnly}
                        InputLabelProps={{ shrink: true }}
                        value={results.logDate || ''} onChange={e => onChange({ ...results, logDate: e.target.value })} />
                </Grid>
            </Grid>

            <Grid container spacing={0.5}>
                <Grid item xs={6}>
                    {col1.map((item) => renderItem(item))}
                </Grid>
                <Grid item xs={6}>
                    {col2.map((item) => renderItem(item))}
                </Grid>
            </Grid>

            <Box sx={{ position: 'relative', mt: 2 }}>
                <TextField
                    label="Comments / Repairs needed"
                    size="small" fullWidth multiline rows={3} disabled={isReadOnly}
                    value={results.comments || ''}
                    onChange={e => onChange({ ...results, comments: e.target.value })}
                />
                {!isReadOnly && dictationSupported && (
                    <IconButton onClick={toggleCommentsDictation} color={isListening ? 'error' : 'primary'}
                        size="small" sx={{ position: 'absolute', right: 4, top: 4 }}>
                        {isListening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                    </IconButton>
                )}
            </Box>

            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f1f8e9', borderRadius: 1, border: '1px solid #c5e1a5' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <TextField label="Inspector" size="small" fullWidth value={results.maintenanceInspector || ''}
                        InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }}
                        onClick={() => { if (!isReadOnly) setInspectorModalOpen(true); }}
                        sx={{ cursor: isReadOnly ? 'default' : 'pointer', '& .MuiInputBase-input': { cursor: isReadOnly ? 'default' : 'pointer' } }} />
                </Box>
                <Typography sx={{ fontSize: '0.82rem', fontStyle: 'italic', color: '#33691e' }}>
                    I, <strong>{results.maintenanceInspector || '___'}</strong>, have inspected said equipment this month of <strong>{monthName}</strong>, Year <strong>{year}</strong> and found all items listed to be in good working order and SAFE Operation.
                </Typography>
            </Box>

            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, fontWeight: 600 }}>
                ALL DEFECTIVE ITEMS SHALL BE REPORTED IMMEDIATELY TO SUPERVISOR
            </Typography>

            <SelectSingleEmployeeModal open={inspectorModalOpen} onClose={() => setInspectorModalOpen(false)}
                onSelect={(emp) => { onChange({ ...results, maintenanceInspector: emp.FullName || emp.fullName || '' }); setInspectorModalOpen(false); }} />
        </Box>
    );
}
